import { ASSETS_DOMAIN } from '@/constants'
import numberFormat from 'locutus/php/strings/number_format'
import { default as CardValidator, number } from 'card-validator'
import {
  PhoneNumberType,
  PhoneNumberFormat,
  PhoneNumberUtil,
} from 'google-libphonenumber'
import slug from 'slug'
import hash from 'object-hash'
import cloneDeep from 'clone-deep'
import Color from 'color'

const phoneUtil = PhoneNumberUtil.getInstance()

slug.defaults.mode = 'rfc3986'

export function clone(value) {
  if ('structuredClone' in window)
    try {
      return structuredClone(value)
    } catch (e) {
      // Ignore
    }

  return cloneDeep(value)
}

export { v7 as uuid, validate as isValidUUID } from 'uuid'
export { isValid as validateCpf } from '@fnando/cpf'
export { deepEqual as equal } from 'fast-equals'
export { default as debounce } from 'debounce'
export { default as deepForEach } from 'deep-for-each'
export { slug, hash }

export function omit(object, keys = []) {
  const result = {}

  for (const key in object) if (!keys.includes(key)) result[key] = object[key]

  return result
}

export function fullTrim(str) {
  return str?.replace?.(/\s+/g, ' ')?.trim?.() ?? str
}

export function sanitizeFullName(fullName) {
  // Palavras que devem ser mantidas em minúsculas
  const minorWords = ['de', 'da', 'do', 'dos', 'das', 'e']

  // Remove caracteres que não são letras, acentos ou espaços
  fullName = fullName.replace(/[^a-zA-ZÀ-ÿ\s]/g, '')

  // Remove espaços extras antes, depois e duplicados entre as palavras
  fullName = fullName.trim().replace(/\s+/g, ' ')

  // Capitaliza a primeira letra de cada palavra e coloca o restante em minúsculas
  fullName = fullName.replace(
    /(^|\s)(\S+)/g,
    (match, space, word) =>
      `${space}${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`,
  )

  // Converte palavras específicas para minúsculas, se não forem a primeira palavra
  fullName = fullName.replace(
    new RegExp(`\\b(${minorWords.join('|')})\\b`, 'gi'),
    (minorWord, index) => (index === 0 ? minorWord : minorWord.toLowerCase()),
  )

  return fullName
}

export function getPhoneNumber(phone, region = 'BR') {
  try {
    if (typeof phone !== 'string') return phone

    return phoneUtil.parse(phone, region)
  } catch (e) {
    return phone
  }
}

export function getPhoneNumberType(phone, region = 'BR') {
  const phoneNumber = getPhoneNumber(phone, region)

  return phoneUtil.getNumberType(phoneNumber)
}

export function sanitizePhoneNumber(phoneNumber) {
  try {
    if (!phoneNumber) return phoneNumber

    phoneNumber = getPhoneNumber(phoneNumber)

    return phoneUtil.format(phoneNumber, PhoneNumberFormat.E164)
  } catch (e) {
    return phoneNumber
  }
}

export function isMobilePhoneNumber(phone) {
  phone = sanitizePhoneNumber(phone)
  // console.log('phone[6]', phone[5])
  if (phone.length === 13 && phone[5] >= 6 && phone[5] <= 9)
    phone = phone.slice(0, 5).concat(9).concat(phone.slice(5))

  return getPhoneNumberType(phone) === PhoneNumberType.MOBILE
}

export function isPossiblePhoneNumber(phoneNumber) {
  phoneNumber = getPhoneNumber(phoneNumber)

  return phoneUtil.isPossibleNumber(phoneNumber)
}

export function getFormattedDateTime(
  dateTime,
  {
    hour = '2-digit',
    minute = '2-digit',
    day = 'numeric',
    month = 'numeric',
    year = 'numeric',
    dateTimeSeparator = ' às ',
    ...otherOptions
  } = {},
) {
  let formattedDateTime = new Date(dateTime).toLocaleString(undefined, {
    hour,
    minute,
    day,
    month,
    year,
    ...otherOptions,
  })

  if (dateTimeSeparator)
    formattedDateTime = formattedDateTime.replace(/\s/g, dateTimeSeparator)

  return formattedDateTime
}

export function isValidPhoneNumber(phoneNumber) {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phoneNumber)

    return phoneUtil.isValidNumber(number)
  } catch (e) {
    return false
  }
}

export function validatePhoneNumber(
  phoneNumber,
  { requireMobilePhone = true } = {},
) {
  try {
    phoneNumber = sanitizePhoneNumber(phoneNumber)

    if (phoneNumber.length === 0) return 'EMPTY_PHONE_NUMBER'

    const isMobilePhoneNumberResult = isMobilePhoneNumber(phoneNumber)

    if (requireMobilePhone === true && !isMobilePhoneNumberResult)
      return 'IS_NOT_MOBILE_NUMBER'

    if (isMobilePhoneNumberResult && phoneNumber.length <= 13)
      return 'NINTH_DIGIT_NOT_SET'

    if (!isPossiblePhoneNumber(phoneNumber) || !isValidPhoneNumber(phoneNumber))
      return 'INVALID_PHONE_NUMBER'

    return true
  } catch (e) {
    console.error(e)
    return 'INVALID_PHONE_NUMBER'
  }
}

export function getFormattedPhoneNumber(phoneNumber, { region = 'BR' } = {}) {
  let formattedPhoneNumber = phoneNumber

  try {
    if (!phoneNumber) return

    phoneNumber = getPhoneNumber(phoneNumber, region)

    formattedPhoneNumber = phoneUtil.format(
      phoneNumber,
      PhoneNumberFormat.NATIONAL,
    )

    if (formattedPhoneNumber.length === 15)
      formattedPhoneNumber = formattedPhoneNumber.replace(' 9', ' 9 ')
    else if (isMobilePhoneNumber(formattedPhoneNumber)) {
      if (formattedPhoneNumber.length === 14)
        formattedPhoneNumber = formattedPhoneNumber.replace(' ', ' 9 ')
    }
  } finally {
    return formattedPhoneNumber
  }
}

export function validateFullName(fullName) {
  fullName = sanitizeFullName(fullName)

  if (fullName.length === 0) return 'EMPTY_FULL_NAME'

  const INVALID_FULL_NAME = 'INVALID_FULL_NAME'

  if (fullName.includes(' ') === false) return 'COMPOUND_NAME_NOT_PRESENT'

  const fullNameWords = fullName.split(' ')
  const fullNameLastWord = fullNameWords[fullNameWords.length - 1]

  if (fullNameLastWord.length <= 1) return INVALID_FULL_NAME

  const fullNameWordsWithOneChar = fullNameWords.filter(
    word => word.replace(/\./g, '').length === 1,
  )
  const enabledOneChars = ['E', 'O', 'D', 'A']
  const hasWordsWithConsonant = fullNameWordsWithOneChar.some(
    word => !enabledOneChars.includes(word),
  )

  if (hasWordsWithConsonant) return INVALID_FULL_NAME

  return true
}

export function isValidEmail(email) {
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  )
}

export function validateEmail(email) {
  if (!email) return 'EMPTY_EMAIL'
  if (isValidEmail(email) === false) return 'INVALID_EMAIL'

  return true
}

export function getSetting(settings = [], key) {
  return settings.find(setting => setting.key === key)
}

export function getSettingValue(settings = [], key) {
  return getSetting(settings, key)?.value
}

export function accentsTidy(value) {
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

export function sanitizePaymentCardHolderName(holderName = '') {
  holderName = holderName || ''
  holderName = fullTrim(holderName)
  holderName = slug(holderName, {
    replacement: ' ',
  })
  holderName = holderName.toUpperCase()

  return holderName
}

export function validatePaymentCardNumber(paymentCardNumber) {
  if (!paymentCardNumber) return 'EMPTY_NUMBER'

  const { isValid } = number(paymentCardNumber)

  return isValid
}

export function validatePaymentCardExpiry(expiry) {
  const { month, year } = getPaymentCardExpiry(expiry)

  if (!month && !year) return 'EMPTY'

  if (!month) return 'MONTH_NOT_DEFINED'

  if (!year) return 'YEAR_NOT_DEFINED'

  if (month < 1 || month > 12) return 'INVALID_MONTH'

  const now = new Date()
  const currentYear = now.getFullYear()

  if (year < currentYear) return 'INVALID_YEAR'

  const currentMonth = now.getMonth() + 1

  if (year === currentYear && month < currentMonth) return 'INVALID_EXPIRY'

  return true
}

export function getPaymentCardExpiry(expiry) {
  let month, year

  switch (typeof expiry) {
    case 'object':
      month = expiry?.month
      year = expiry?.year
      break

    case 'string':
      const split = (expiry ?? '').toString().split('/', 2)

      month = split?.[0]
      year = split?.[1]
      break
  }

  if (month) month = month.toString().trim().padStart(2, '0')

  if (year) {
    const currentYear = new Date().getFullYear().toString()
    const firstNumbersCurrentYear = currentYear.substring(0, 2)

    year = Number.parseInt(
      year
        .toString()
        .trim()
        .padStart(currentYear.length, firstNumbersCurrentYear),
    )
  }

  return {
    month,
    year,
  }
}

export function getPaymentCardExpiryFormatted(expiry) {
  let { month, year } = getPaymentCardExpiry(expiry)

  if (!month || !year) return

  month = (month ?? '').toString().trim().padStart(2, '0')

  return `${month}/${year}`
}

export function getPaymentCardBrandName({ number }) {
  return CardValidator.number(number)?.card?.niceType
}

export function getPaymentCardBrandImage({ number }) {
  const paymentCardBrandName = getPaymentCardBrandName({ number })
  const cardBrandSlug = slug(paymentCardBrandName)

  return getPaymentFormImage(cardBrandSlug)
}

export function getPaymentCardNumberLastDigits({ number }) {
  number = number.toString()

  return number.substr(number.length - 4)
}

export function getPaymentCardId({ number, holderName, expiry }) {
  const normalizedPaymentCard = {
    number,
    expiry: getPaymentCardExpiry(expiry),
    holderName,
  }

  return hash(normalizedPaymentCard)
}

export function getOnlyNumbers(value) {
  return (value ?? '').toString().replace(/[^0-9]/g, '')
}

export function validateZipCode(zipCode) {
  zipCode = getOnlyNumbers(zipCode)

  return zipCode.length === 8
}

export function getFormattedCurrency(value, { currency = 'R$' } = {}) {
  return `${currency} ${numberFormat(value, 2, ',', '.')}`
}

export function hasOnlyNumbers(value) {
  return /^[0-9]+$/.test(value.toString())
}

export function validatePaymentCardSecurityCode(securityCode) {
  if (!securityCode) return 'EMPTY_SECURITY_CODE'

  securityCode = securityCode.toString()

  if (securityCode.length < 3) return 'INVALID_LENGTH_SECURITY_CODE'

  if (hasOnlyNumbers(securityCode) === false) return 'INVALID_SECURITY_CODE'

  return true
}

export function toLocalISOString(date) {
  const offset = -date.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const pad = n => String(Math.floor(Math.abs(n))).padStart(2, '0')

  const hours = pad(offset / 60)
  const minutes = pad(offset % 60)

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${hours}:${minutes}`
  )
}

export function defaultFilterFunction(
  { isVisible, startAt, endAt, schedules = [] },
  { ignoreIsVisible = false } = {},
) {
  if (!ignoreIsVisible && isVisible === false) return false

  const now = new Date()

  if (startAt && now < new Date(startAt)) return false
  if (endAt && now > new Date(endAt)) return false

  const hasActiveScheduleNow =
    !schedules ||
    schedules?.length === 0 ||
    (schedules ?? []).some(
      ({ active, startDate, endDate, weekdays }) =>
        active !== false &&
        (!startDate || new Date(startDate) <= now) &&
        (!endDate || new Date(endDate) >= now) &&
        (!weekdays ||
          weekdays?.length === 0 ||
          weekdays.some(({ weekday, startTime, endTime }) => {
            if (weekday !== now.getDay()) return false

            if (startTime || endTime) {
              const currentDate = toLocalISOString(now).split('T', 2)[0]

              if (startTime) {
                startTime = new Date(`${currentDate}T${startTime}-03:00`)

                if (now < startTime) return false
              }

              if (endTime) {
                endTime = new Date(`${currentDate}T${endTime}-03:00`)

                if (now > endTime) return false
              }
            }

            return true
          })),
    )

  if (hasActiveScheduleNow === false) return false

  return true
}
export function defaultSortFunction(i1, i2, callback) {
  if (typeof callback === 'function') return callback(i1, i2)

  if (i1?.active === false && i2?.active === true) return 1
  if (i1?.active === true && i2?.active === false) return -1

  const i1OrderNum = i1?.orderNum ?? Number.POSITIVE_INFINITY
  const i2OrderNum = i2?.orderNum ?? Number.POSITIVE_INFINITY

  if (i1OrderNum > i2OrderNum) return 1
  if (i1OrderNum < i2OrderNum) return -1

  return 0
}

export function isUrl(url) {
  try {
    return Boolean(new URL(url))
  } catch (e) {
    return false
  }
}

export function getPaymentFormImage(paymentForm) {
  let paymentFormImage =
    typeof paymentForm === 'string' ? paymentForm : paymentForm?.image

  if (!paymentFormImage) paymentFormImage = slug(paymentForm?.name)
  else if (isUrl(paymentFormImage)) return paymentFormImage

  if (paymentFormImage.indexOf('.') === -1)
    paymentFormImage = `${paymentFormImage}.svg`

  paymentFormImage = `https://${ASSETS_DOMAIN}/images/payment-forms/${paymentFormImage}`

  return paymentFormImage
}

export function calculate(value, operation) {
  if (operation === undefined) return value
  if (typeof operation !== 'string') operation = operation?.toString?.()

  let result = value
  let operator
  let operationValue = operation

  const hasPercentage = operation.endsWith('%')
  const firstChar = operation.charAt(0)

  if (['-', '+', '*', '/', '='].includes(firstChar)) {
    operator = firstChar
    operationValue = operation.slice(1)
  }

  operationValue = operationValue.replace(/%/g, '')
  operationValue = Number(operationValue)

  if (hasPercentage === true) operationValue = (operationValue * value) / 100

  switch (operator) {
    case '+':
      result += operationValue
      break

    case '-':
      result -= operationValue
      break

    case '*':
      result *= operationValue
      break

    case '/':
      result /= operationValue
      break

    default:
      result = operationValue
      break
  }

  return result
}

export function getProductImages({ images = [] }) {
  return images.map(image => image?.thumbnail?.url ?? image)
}

export function getOrderItemImages({ images, products = [] }) {
  return images ?? products.flatMap(getProductImages)
}

export async function importScript(
  src,
  { crossOrigin, defer, async = true } = {},
) {
  return new Promise((resolve, reject) => {
    const scriptElement = document.createElement('script')

    scriptElement.src = src
    scriptElement.async = async
    scriptElement.onload = resolve
    scriptElement.onerror = reject

    if (crossOrigin) scriptElement.crossOrigin = crossOrigin

    if (defer !== undefined) scriptElement.defer = defer

    document.body.appendChild(scriptElement)
  })
}

export async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function naturalJoin(
  value,
  { separator = ', ', conjunction = ' e ' } = {},
) {
  let string = ''

  if (Array.isArray(value)) {
    const count = value.length

    if (count === 1) string = value[0]
    else if (count > 1) {
      const list = value.slice(0, -1)
      const last = value[count - 1]

      string = `${list.join(separator)}${conjunction}${last}`
    }
  }

  return string
}
export function isEmptyObject(object) {
  try {
    return Object.keys(object).length === 0
  } catch (e) {
    return false
  }
}

export function updateObject(currentObject, newObject) {
  for (const n in newObject)
    if (currentObject.hasOwnProperty(n) === false) delete currentObject[n]

  for (const n in newObject) currentObject[n] = newObject[n]

  return newObject
}

export function getArrayRandomValue(array) {
  return array[Math.floor(Math.random() * array.length)]
}

export function getTextColor(color) {
  let textColor = '#000'

  color = color?.trim?.()

  if (Color(color).luminosity() <= 0.4) textColor = '#fff'

  return textColor
}

export async function getSupportWhatsAppURL(cityId) {
  const { settings = [] } = await app.callApi({
    method: 'GET',
    version: 1,
    action: `cities/${cityId}`,
  })
  const setting = settings.find(({ key }) => key === 'whatsappUrl')

  return setting?.value
}

export function formatSeconds(totalSeconds) {
  totalSeconds = Math.round(totalSeconds)

  let minutes = Math.floor(totalSeconds / 60)
  let seconds = totalSeconds % 60

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0',
  )}`
}

export function formatPercentage(value, { maximumFractionDigits = 2 } = {}) {
  if (typeof value === 'number') value /= 100

  value = value.toLocaleString('pt-BR', {
    style: 'percent',
    maximumFractionDigits,
  })

  return value
}

export function mergeObjects(object1, object2) {
  const result = {}

  function copyProperties(source, target) {
    Object.getOwnPropertyNames(source).forEach(prop => {
      const propertyDescriptor = Object.getOwnPropertyDescriptor(source, prop)
      Object.defineProperty(target, prop, propertyDescriptor)
    })
  }

  copyProperties(object1, result)
  copyProperties(object2, result)

  return result
}

export function hashObject(object, ignoreProps = ['hash']) {
  let clonedObject = clone(object)

  ignoreProps.forEach(prop => delete clonedObject[prop])

  return hash(clonedObject)
}

export function fixFloat(value, precision = 2) {
  return Number(value.toFixed(precision))
}

export function getValue(
  value,
  { acceptTypes = ['number', 'string'], dictionary = {} } = {},
) {
  const selfFunction = getValue
  const {
    companySectionPortionId,
    companySectionSizeId,
    numberProducts,
    numberUniqueProducts,
  } = dictionary

  let nextValue

  nextValue = value?.sizes?.[companySectionSizeId]

  if (companySectionSizeId !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  nextValue = value?.portions?.[companySectionPortionId]

  if (companySectionPortionId !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  nextValue = value?.numberProducts?.[numberProducts]

  if (numberProducts !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  nextValue = value?.numberUniqueProducts?.[numberUniqueProducts]

  if (numberUniqueProducts !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  if ([null, undefined].includes(value) || acceptTypes.includes(typeof value))
    return value
}

export function extractValues(obj) {
  let values = []

  for (let key in obj) {
    const value = obj[key]

    if (typeof value === 'object') {
      values = values.concat(extractValues(value))
    } else {
      values.push(value)
    }
  }

  return values
}

export function detectOperatingSystem() {
  const platform = navigator.platform?.toLowerCase?.() ?? ''
  const userAgent = navigator.userAgent.toLowerCase() ?? ''

  let operatingSystem = 'Unknown'

  if (platform.includes('android') || userAgent.includes('android')) {
    operatingSystem = 'Android'
  } else if (platform.includes('iphone') || userAgent.includes('iphone')) {
    operatingSystem = 'iOS'
  } else if (platform.includes('win')) {
    operatingSystem = 'Windows'
  } else if (platform.includes('mac')) {
    operatingSystem = 'MacOS'
  } else if (platform.includes('linux')) {
    operatingSystem = 'Linux'
  }

  return operatingSystem
}

export function toRadians(degrees) {
  return degrees * (Math.PI / 180)
}

export function haversine(lat1, lon1, lat2, lon2) {
  const deltaLat = toRadians(lat2 - lat1)
  const deltaLon = toRadians(lon2 - lon1)
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(deltaLon / 2) ** 2

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return 6371 * c
}

export function getDistanceBetweenCoordinates(coordinate1, coordinate2) {
  const { latitude: lat1, longitude: lng1 } = coordinate1
  const { latitude: lat2, longitude: lng2 } = coordinate2

  return haversine(lat1, lng1, lat2, lng2)
}

export function formatDistance(distanceInKm) {
  if (distanceInKm < 1) {
    const distanceInMeters = (distanceInKm * 1000).toFixed(0)

    return `${distanceInMeters} m`
  }

  distanceInKm = distanceInKm.toFixed(2)

  return `${distanceInKm} km`
}

export async function clientSupportsAVIF() {
  return new Promise(resolve => {
    const avifImage = new Image()

    avifImage.src =
      'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A='
    avifImage.onload = () => resolve(true)
    avifImage.onerror = () => resolve(false)
  })
}

export function hasNumbers(str) {
  return /\d/.test(str)
}
